import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Alert } from 'react-native';
import { TextInput, Button, Title, Text, Divider, Snackbar, Modal, Portal, IconButton } from 'react-native-paper';
import { useAuth } from '../../context/AuthContext';
import { isValidEmail, isValidPassword, isValidName, isValidPhone } from '../../utils';
import { CommonActions } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MaterialCommunityIcons } from '@expo/vector-icons';

const RegisterScreen = ({ navigation }) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  
  // Persistent form state for web platform
  const [persistentFormState, setPersistentFormState] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  
  // Input refs for direct access
  const nameInputRef = useRef(null);
  const surnameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const confirmPasswordInputRef = useRef(null);
  
  // Password visibility
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  
  // Error state
  const [errors, setErrors] = useState({});
  
  // Snackbar
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Error Modal
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [errorModalTitle, setErrorModalTitle] = useState('Kayıt Hatası');
  
  // Success state
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Auth context
  const { signUp } = useAuth();
  
  // Form verilerinin kalıcılığını sağlamak için localStorage
  useEffect(() => {
    const loadStoredFormData = async () => {
      try {
        const storedData = await AsyncStorage.getItem('registerFormData');
        
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setFormData(parsedData);
          setPersistentFormState(parsedData);
        }
      } catch (error) {
        console.error('Form verilerini yüklerken hata:', error);
      }
    };
    
    loadStoredFormData();
  }, []);

  // Otomatik yönlendirme hook'u kaldırıldı - yalnızca butonla yönlendirme yapılacak

  // Otomatik navigasyon fonksiyonu kaldırıldı
  // Kullanıcı modal üzerindeki butonla yönlendirilecek
  
  // Form verilerini kaydet
  const saveFormData = async (updatedData) => {
    try {
      await AsyncStorage.setItem('registerFormData', JSON.stringify(updatedData));
      setPersistentFormState(updatedData);
    } catch (error) {
      console.error('Form verilerini kaydederken hata:', error);
    }
  };
  
  // Input change handler
  const handleChange = (field, value) => {
    // Telefon numarası için karakter sınırı
    if (field === 'phone' && value.length > 10) {
      return; // 10 karakterden fazlasını engelle
    }
    
    const updatedFormData = { ...formData, [field]: value };
    setFormData(updatedFormData);
    
    // Form verilerini async kaydet
    saveFormData(updatedFormData);
    
    // Clear error when user types
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    
    // Input ref'leri güncelle (web problemi için)
    const inputRefs = {
      name: nameInputRef,
      surname: surnameInputRef,
      email: emailInputRef,
      phone: phoneInputRef,
      password: passwordInputRef,
      confirmPassword: confirmPasswordInputRef
    };
    
    if (inputRefs[field]?.current) {
      inputRefs[field].current.setNativeProps({ text: value });
    }
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Name validation
    if (!formData.name) {
      newErrors.name = 'Ad gereklidir';
      isValid = false;
    } else if (!isValidName(formData.name)) {
      newErrors.name = 'Geçerli bir ad giriniz';
      isValid = false;
    }
    
    // Surname validation
    if (!formData.surname) {
      newErrors.surname = 'Soyad gereklidir';
      isValid = false;
    } else if (!isValidName(formData.surname)) {
      newErrors.surname = 'Geçerli bir soyad giriniz';
      isValid = false;
    }
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email adresi gereklidir';
      isValid = false;
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Geçerli bir email adresi giriniz';
      isValid = false;
    }
    
    // Phone validation - zorunlu alan haline getiriyoruz
    if (!formData.phone) {
      newErrors.phone = 'Telefon numarası gereklidir';
      isValid = false;
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Geçerli bir 10 haneli telefon numarası giriniz';
      isValid = false;
    } else if (formData.phone.length !== 10) {
      newErrors.phone = 'Telefon numarası 10 haneli olmalıdır';
      isValid = false;
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Şifre gereklidir';
      isValid = false;
    } else if (!isValidPassword(formData.password)) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır ve bir özel karakter ile birer küçük, büyük harf içermelidir.';
      isValid = false;
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifre tekrarı gereklidir';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  // Register handler
  const handleRegister = async (e) => {
    // Formu önlem ve sayfa yenilemeyi engelle (web için)
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Form değerlerini sakla
    await saveFormData(formData);
    
    // Form doğrulama
    if (!validateForm()) {
      //console.log('Form validation error');
      
      // DOM ile modal hata gösterim
      const errorDiv = document.createElement('div');
      errorDiv.style.position = 'fixed';
      errorDiv.style.top = '50%';
      errorDiv.style.left = '50%';
      errorDiv.style.transform = 'translate(-50%, -50%)';
      errorDiv.style.backgroundColor = 'white';
      errorDiv.style.padding = '20px';
      errorDiv.style.borderRadius = '10px';
      errorDiv.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';
      errorDiv.style.zIndex = '9999';
      errorDiv.style.maxWidth = '80%';
      errorDiv.style.textAlign = 'center';
      
      const errorTitle = document.createElement('h3');
      errorTitle.innerText = 'Kayıt Formu Hatası';
      errorTitle.style.marginBottom = '10px';
      errorTitle.style.color = '#d32f2f';
      
      const errorText = document.createElement('p');
      errorText.innerText = 'Lütfen form alanlarını kontrol ederek hatalı alanları düzeltiniz.';
      errorText.style.marginBottom = '15px';
      
      const closeButton = document.createElement('button');
      closeButton.innerText = 'Kapat';
      closeButton.style.backgroundColor = '#f44336';
      closeButton.style.color = 'white';
      closeButton.style.border = 'none';
      closeButton.style.padding = '8px 16px';
      closeButton.style.borderRadius = '4px';
      closeButton.style.cursor = 'pointer';
      closeButton.onclick = function() {
        document.body.removeChild(errorDiv);
      };
      
      errorDiv.appendChild(errorTitle);
      errorDiv.appendChild(errorText);
      errorDiv.appendChild(closeButton);
      
      // DOM'a ekle
      document.body.appendChild(errorDiv);
      
      // State güncellemesi
      setIsSuccess(false);
      
      return false;
    }
      
    setIsLoading(true);
    //console.log('Kayıt işlemi başlatılıyor...');
    
    try {
      // Form verisini API'ye gönderilecek şekilde hazırla
      const userData = {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      };
      
      try {
        // API call - signUp'tan dönen yanıtı kontrol et
        const response = await signUp(userData);
        //console.log('Kayıt yanıtı alındı:', response);
        
        // API yanıtı kontrolü
        if (!response) {
          // Hata oluştur ama alert kullanma
          const errorDiv = document.createElement('div');
          errorDiv.style.position = 'fixed';
          errorDiv.style.top = '50%';
          errorDiv.style.left = '50%';
          errorDiv.style.transform = 'translate(-50%, -50%)';
          errorDiv.style.backgroundColor = 'white';
          errorDiv.style.padding = '20px';
          errorDiv.style.borderRadius = '10px';
          errorDiv.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';
          errorDiv.style.zIndex = '9999';
          errorDiv.style.maxWidth = '80%';
          errorDiv.style.textAlign = 'center';
          
          const errorTitle = document.createElement('h3');
          errorTitle.innerText = 'Sunucu Hatası';
          errorTitle.style.marginBottom = '10px';
          errorTitle.style.color = '#d32f2f';
          
          const errorText = document.createElement('p');
          errorText.innerText = 'Sunucudan yanıt alınamadı. Lütfen daha sonra tekrar deneyiniz.';
          errorText.style.marginBottom = '15px';
          
          const closeButton = document.createElement('button');
          closeButton.innerText = 'Kapat';
          closeButton.style.backgroundColor = '#f44336';
          closeButton.style.color = 'white';
          closeButton.style.border = 'none';
          closeButton.style.padding = '8px 16px';
          closeButton.style.borderRadius = '4px';
          closeButton.style.cursor = 'pointer';
          closeButton.onclick = function() {
            document.body.removeChild(errorDiv);
          };
          
          errorDiv.appendChild(errorTitle);
          errorDiv.appendChild(errorText);
          errorDiv.appendChild(closeButton);
          
          // DOM'a ekle
          document.body.appendChild(errorDiv);
          
          setIsLoading(false);
          return false;
        }
        
        // 400/401 hataları için özel işlem
        if (response && (response.status === 400 || response.status === 401)) {
          //console.log('400/401 error response caught in handleRegister:', response);
          
          // HATA DURUMU - hata mesajlarını düzenle
          let errorMessage = response.message || "Kayıt başarısız oldu";
          
          // API'nin errors objesinden hata mesajlarını çıkar
          if (response.errors && Object.keys(response.errors).length > 0) {
            // Tüm hata mesajlarını al
            const errorValues = Object.values(response.errors);
            
            // Hata mesajlarını virgülle ayırarak birleştir (tek mesaj varsa virgül olmaz)
            errorMessage = errorValues.join(', ');
            
            //console.log('Birleştirilmiş hata mesajları:', errorMessage);
          }
          
          // 1. Direkt DOM manipulation ile hata göster - en garantili yöntem
          const errorDiv = document.createElement('div');
          errorDiv.style.position = 'fixed';
          errorDiv.style.top = '50%';
          errorDiv.style.left = '50%';
          errorDiv.style.transform = 'translate(-50%, -50%)';
          errorDiv.style.backgroundColor = 'white';
          errorDiv.style.padding = '20px';
          errorDiv.style.borderRadius = '10px';
          errorDiv.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';
          errorDiv.style.zIndex = '9999';
          errorDiv.style.maxWidth = '80%';
          errorDiv.style.textAlign = 'center';
          
          const errorTitle = document.createElement('h3');
          errorTitle.innerText = 'Kayıt İşlemi Başarısız';
          errorTitle.style.marginBottom = '10px';
          errorTitle.style.color = '#d32f2f';
          
          const errorText = document.createElement('p');
          errorText.innerText = errorMessage;
          errorText.style.marginBottom = '15px';
          
          const closeButton = document.createElement('button');
          closeButton.innerText = 'Kapat';
          closeButton.style.backgroundColor = '#f44336';
          closeButton.style.color = 'white';
          closeButton.style.border = 'none';
          closeButton.style.padding = '8px 16px';
          closeButton.style.borderRadius = '4px';
          closeButton.style.cursor = 'pointer';
          closeButton.onclick = function() {
            document.body.removeChild(errorDiv);
          };
          
          errorDiv.appendChild(errorTitle);
          errorDiv.appendChild(errorText);
          errorDiv.appendChild(closeButton);
          
          // DOM'a ekle
          document.body.appendChild(errorDiv);
          
          // Alert kaldırıldı - sadece DOM tabanlı modal kullanılıyor
          
          // 3. Form alanlarını koruma
          Object.keys(formData).forEach(field => {
            const inputRefs = {
              name: nameInputRef,
              surname: surnameInputRef,
              email: emailInputRef,
              phone: phoneInputRef,
              password: passwordInputRef,
              confirmPassword: confirmPasswordInputRef
            };
            
            if (inputRefs[field]?.current) {
              inputRefs[field].current.setNativeProps({ text: formData[field] });
            }
          });
          
          // Form verilerini lokalde sakla
          await saveFormData(formData);
          
          setIsLoading(false); // Yükleme durumunu kapat
          return false;
        }
      } catch (error) {
        // Hata yakalandı
        console.error('Kayıt sırasında hata:', error);
        
        // Hata modalini DOM ile göster
        const errorDiv = document.createElement('div');
        errorDiv.style.position = 'fixed';
        errorDiv.style.top = '50%';
        errorDiv.style.left = '50%';
        errorDiv.style.transform = 'translate(-50%, -50%)';
        errorDiv.style.backgroundColor = 'white';
        errorDiv.style.padding = '20px';
        errorDiv.style.borderRadius = '10px';
        errorDiv.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';
        errorDiv.style.zIndex = '9999';
        errorDiv.style.maxWidth = '80%';
        errorDiv.style.textAlign = 'center';
        
        const errorTitle = document.createElement('h3');
        errorTitle.innerText = 'Kayıt Hatası';
        errorTitle.style.marginBottom = '10px';
        errorTitle.style.color = '#d32f2f';
        
        const errorText = document.createElement('p');
        errorText.innerText = 'Kayıt sırasında hata: ' + (error.message || 'Bilinmeyen hata');
        errorText.style.marginBottom = '15px';
        
        const closeButton = document.createElement('button');
        closeButton.innerText = 'Kapat';
        closeButton.style.backgroundColor = '#f44336';
        closeButton.style.color = 'white';
        closeButton.style.border = 'none';
        closeButton.style.padding = '8px 16px';
        closeButton.style.borderRadius = '4px';
        closeButton.style.cursor = 'pointer';
        closeButton.onclick = function() {
          document.body.removeChild(errorDiv);
        };
        
        errorDiv.appendChild(errorTitle);
        errorDiv.appendChild(errorText);
        errorDiv.appendChild(closeButton);
        
        // DOM'a ekle
        document.body.appendChild(errorDiv);
        
        setIsLoading(false);
        return false;
      }
      
      // Burası sadece başarılı kayıt durumunda çalışacak
      // Başarısız kayıtlar zaten yukarıdaki if bloklarında return ile sonlandırılıyor
      //console.log('Kayıt başarılı:', response);
      
      // Web platformu için DOM tabanlı başarılı kayıt modalı
      if (typeof document !== 'undefined') {
        const successDiv = document.createElement('div');
        successDiv.style.position = 'fixed';
        successDiv.style.top = '50%';
        successDiv.style.left = '50%';
        successDiv.style.transform = 'translate(-50%, -50%)';
        successDiv.style.backgroundColor = 'white';
        successDiv.style.padding = '20px';
        successDiv.style.borderRadius = '10px';
        successDiv.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';
        successDiv.style.zIndex = '9999';
        successDiv.style.maxWidth = '80%';
        successDiv.style.textAlign = 'center';
        
        const successTitle = document.createElement('h3');
        successTitle.innerText = 'Kayıt Başarılı';
        successTitle.style.marginBottom = '10px';
        successTitle.style.color = '#4CAF50';
        
        const successText = document.createElement('p');
        successText.innerText = 'Kayıt işleminiz başarıyla tamamlandı! Lütfen giriş yapınız.';
        successText.style.marginBottom = '15px';
        
        const loginButton = document.createElement('button');
        loginButton.innerText = 'Giriş Sayfasına Git';
        loginButton.style.backgroundColor = '#4CAF50';
        loginButton.style.color = 'white';
        loginButton.style.border = 'none';
        loginButton.style.padding = '8px 16px';
        loginButton.style.borderRadius = '4px';
        loginButton.style.cursor = 'pointer';
        loginButton.onclick = function() {
          document.body.removeChild(successDiv);
          // Login ekranına yönlendir
          navigation.reset({
            index: 0,
            routes: [{ 
              name: 'Login', 
              params: { message: 'Kayıt işleminiz başarıyla tamamlandı, lütfen giriş yapınız.' } 
            }]
          });
        };
        
        successDiv.appendChild(successTitle);
        successDiv.appendChild(successText);
        successDiv.appendChild(loginButton);
        
        // DOM'a ekle
        document.body.appendChild(successDiv);
      } else {
        // React Native modalı için state güncellemesi
        setIsSuccess(true);
        setErrorModalTitle('Kayıt Başarılı');
        setErrorMessages(['Kayıt işleminiz başarıyla tamamlandı! Lütfen giriş yapınız.']);
        setErrorModalVisible(true);
      }
      
      // Form verilerini temizle
      const emptyForm = {
        name: '',
        surname: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      };
      
      setFormData(emptyForm);
      
      // localStorage'dan kayıtlı verileri temizle
      await AsyncStorage.removeItem('registerFormData');
      
    } catch (error) {
      // Genel hata durumu - önceki tüm işlemlerden geçemeyen hatalar
      console.error('Beklenmeyen kayıt hatası:', error);
      setIsSuccess(false);
      setErrorModalTitle('Kayıt İşlemi Başarısız');
      setErrorMessages([error.message || 'Beklenmeyen bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.']);
      setErrorModalVisible(true);
      
      // Input ref'lerini yeniden ayarla
      Object.keys(formData).forEach(field => {
        const inputRefs = {
          name: nameInputRef,
          surname: surnameInputRef,
          email: emailInputRef,
          phone: phoneInputRef,
          password: passwordInputRef,
          confirmPassword: confirmPasswordInputRef
        };
        
        if (inputRefs[field]?.current) {
          inputRefs[field].current.setNativeProps({ text: formData[field] });
        }
      });
      
      // Form verilerini kaydet
      await saveFormData(formData);
      
    } finally {
      setIsLoading(false);
    }
    
    // Form işlemi sonrası varsayılan davranışı engelle
    return false;
  };
  
  // Parse validation errors from API
  const handleValidationErrors = (errors) => {
    const newErrors = {};
    const errorList = [];
    
    // Loop through errors and format them for display
    for (const [field, message] of Object.entries(errors)) {
      newErrors[field] = message;
      errorList.push(`${field}: ${message}`);
    }
    
    // Set field errors
    setErrors(newErrors);
    
    // Show error modal with all errors
    setErrorMessages(errorList);
    setErrorModalVisible(true);
  };

  // Hata mesajlarını göster
  const showErrorMessages = (error) => {
    // Hata mesajları listesi
    let messageList = [];
    
    // API'den gelen hata alanlarını işle
    if (error && error.errors && Object.keys(error.errors).length > 0) {
      // Form alanlarına hataları yansıt
      const fieldErrors = { ...errors };
      
      // Hata mesajlarını oluştur
      Object.entries(error.errors).forEach(([field, message]) => {
        // Özel alan eşleştirmeleri
        if (field === 'username') {
          fieldErrors.name = message;
          messageList.push(`Ad/Soyad: ${message}`);
        } else if (field === 'phone_number') {
          fieldErrors.phone = message;
          messageList.push(`Telefon: ${message}`);
        } else if (field in formData) {
          // Formda olan alanlar
          fieldErrors[field] = message;
          
          // Türkçe alan adı
          const fieldNames = {
            name: 'Ad',
            surname: 'Soyad',
            email: 'E-posta',
            password: 'Şifre'
          };
          
          messageList.push(`${fieldNames[field] || field}: ${message}`);
        } else {
          // Diğer hatalar
          messageList.push(`${field}: ${message}`);
        }
      });
      
      // Form hatalarını güncelle
      setErrors(fieldErrors);
      
    } else {
      // Genel hata mesajı
      messageList.push(error?.message || 'Kayıt işlemi başarısız oldu. Lütfen daha sonra tekrar deneyin.');
    }
    
    // Snackbar ile kısa mesaj göster
    setSnackbarMessage('Kayıt işlemi başarısız oldu');
    setSnackbarVisible(true);
    
    // Modal ile detaylı hata göster
    setErrorMessages(messageList);
    setErrorModalTitle('Kayıt Hatası');
    setErrorModalVisible(true);
  };
  
  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.headerContainer}>
          <Title style={styles.title}>Yeni Hesap Oluştur</Title>
          <Text style={styles.subtitle}>Kişisel bilgilerinizi girin</Text>
        </View>
        
        <View style={styles.formContainer}>
          {/* Kişisel Bilgiler */}
          <TextInput
            ref={nameInputRef}
            label="Ad"
            value={formData.name}
            onChangeText={(value) => handleChange('name', value)}
            mode="outlined"
            error={!!errors.name}
            style={styles.input}
            left={<TextInput.Icon icon="account" color="rgb(125, 76, 236)" />}
            theme={{ 
              colors: { 
                primary: 'rgb(125, 76, 236)', 
                underlineColor: 'transparent',
                background: 'white',
                text: '#000',
                placeholder: '#555'
              } 
            }}
            outlineColor="#ccc"
            placeholderTextColor="#555"
            defaultValue={persistentFormState.name}
          />
          {errors.name ? <Text style={styles.errorText}>{errors.name}</Text> : null}
          
          <TextInput
            ref={surnameInputRef}
            label="Soyad"
            value={formData.surname}
            onChangeText={(value) => handleChange('surname', value)}
            mode="outlined"
            error={!!errors.surname}
            style={styles.input}
            left={<TextInput.Icon icon="account" color="rgb(125, 76, 236)" />}
            theme={{ 
              colors: { 
                primary: 'rgb(125, 76, 236)', 
                underlineColor: 'transparent',
                background: 'white',
                text: '#000',
                placeholder: '#555'
              } 
            }}
            outlineColor="#ccc"
            placeholderTextColor="#555"
            defaultValue={persistentFormState.surname}
          />
          {errors.surname ? <Text style={styles.errorText}>{errors.surname}</Text> : null}
          
          <TextInput
            ref={emailInputRef}
            label="Email"
            value={formData.email}
            onChangeText={(value) => handleChange('email', value)}
            mode="outlined"
            keyboardType="email-address"
            autoCapitalize="none"
            error={!!errors.email}
            style={styles.input}
            left={<TextInput.Icon icon="email" color="rgb(125, 76, 236)" />}
            theme={{ 
              colors: { 
                primary: 'rgb(125, 76, 236)', 
                underlineColor: 'transparent',
                background: 'white',
                text: '#000',
                placeholder: '#555'
              } 
            }}
            outlineColor="#ccc"
            placeholderTextColor="#555"
            defaultValue={persistentFormState.email}
          />
          {errors.email ? <Text style={styles.errorText}>{errors.email}</Text> : null}
          
          <TextInput
            ref={phoneInputRef}
            label="Telefon *"
            value={formData.phone}
            onChangeText={(text) => handleChange('phone', text)}
            keyboardType="phone-pad"
            mode="outlined"
            style={styles.input}
            error={!!errors.phone}
            left={<TextInput.Icon icon="phone" color="rgb(125, 76, 236)" />}
            theme={{ 
              colors: { 
                primary: 'rgb(125, 76, 236)', 
                underlineColor: 'transparent',
                background: 'white',
                text: '#000',
                placeholder: '#555'
              } 
            }}
            outlineColor="#ccc"
            placeholderTextColor="#555"
            defaultValue={persistentFormState.phone}
          />
          {errors.phone && (
            <Text style={styles.errorText}>{errors.phone}</Text>
          )}
          
          <Divider style={styles.divider} />
          
          {/* Şifre Bilgileri */}
          <TextInput
            ref={passwordInputRef}
            label="Şifre"
            value={formData.password}
            onChangeText={(value) => handleChange('password', value)}
            mode="outlined"
            secureTextEntry={!passwordVisible}
            error={!!errors.password}
            style={styles.input}
            left={<TextInput.Icon icon="lock" color="rgb(125, 76, 236)" />}
            theme={{ 
              colors: { 
                primary: 'rgb(125, 76, 236)', 
                underlineColor: 'transparent',
                background: 'white',
                text: '#000',
                placeholder: '#555'
              } 
            }}
            outlineColor="#ccc"
            placeholderTextColor="#555"
            defaultValue={persistentFormState.password}
            right={
              <TextInput.Icon
                icon={passwordVisible ? 'eye-off' : 'eye'}
                onPress={() => setPasswordVisible(!passwordVisible)}
                color="rgb(125, 76, 236)"
              />
            }
          />
          {errors.password ? <Text style={styles.errorText}>{errors.password}</Text> : null}
          
          <TextInput
            ref={confirmPasswordInputRef}
            label="Şifre Tekrarı"
            value={formData.confirmPassword}
            onChangeText={(value) => handleChange('confirmPassword', value)}
            mode="outlined"
            secureTextEntry={!confirmPasswordVisible}
            error={!!errors.confirmPassword}
            style={styles.input}
            left={<TextInput.Icon icon="lock-check" color="rgb(125, 76, 236)" />}
            defaultValue={persistentFormState.confirmPassword}
            theme={{ 
              colors: { 
                primary: 'rgb(125, 76, 236)', 
                underlineColor: 'transparent',
                background: 'white',
                text: '#000',
                placeholder: '#555'
              } 
            }}
            outlineColor="#ccc"
            placeholderTextColor="#555"
            right={
              <TextInput.Icon
                icon={confirmPasswordVisible ? 'eye-off' : 'eye'}
                onPress={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                color="rgb(125, 76, 236)"
              />
            }
          />
          {errors.confirmPassword ? <Text style={styles.errorText}>{errors.confirmPassword}</Text> : null}
          
          <View style={styles.buttonsContainer}>
            <Button
              mode="contained"
              onPress={(e) => {
                // Sayfa yenilenmesini engelle
                if (e && e.preventDefault) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                handleRegister(e);
              }}
              style={styles.registerButton}
              loading={isLoading}
              disabled={isLoading}
              color="rgb(125, 76, 236)"
            >
              Kayıt Ol
            </Button>

            <View style={styles.loginContainer}>
              <Text style={styles.loginText}>Zaten hesabınız var mı?</Text>
              <Button
                mode="text"
                onPress={() => navigation.navigate('Login')}
                style={styles.loginButton}
                disabled={isLoading}
                color="rgb(125, 76, 236)"
              >
                Giriş Yap
              </Button>
            </View>
          </View>
        </View>
      </ScrollView>
      
      {/* Snackbar bildirim */}
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={[
          styles.snackbar,
          isSuccess ? styles.successSnackbar : styles.errorSnackbar
        ]}
        wrapperStyle={{ zIndex: 1000 }}
        action={{
          label: 'TAMAM',
          onPress: () => setSnackbarVisible(false),
          color: 'white'
        }}
      >
        {snackbarMessage}
      </Snackbar>
      
      {/* Hata/Başarı Bildirimi - Web uyumlu basit yaklaşım */}
      {errorModalVisible && (
        <View 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            elevation: 9999,
          }}
        >
          <View style={{
            width: '90%',
            maxWidth: 500,
            backgroundColor: 'white',
            borderRadius: 10,
            padding: 20,
            alignItems: 'center',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
            elevation: 5,
          }}>
            <MaterialCommunityIcons 
              name={isSuccess ? "check-circle-outline" : "alert-circle-outline"} 
              size={70} 
              color={isSuccess ? "#4CAF50" : "#FF9800"}
            />
            
            <Text style={{
              fontSize: 20,
              fontWeight: 'bold',
              color: '#333',
              marginVertical: 15,
              textAlign: 'center',
            }}>
              {isSuccess ? "Kayıt Başarılı" : "Kayıt İşlemi Başarısız"}
            </Text>
            
            <View style={{
              width: '100%',
              maxHeight: 150,
              marginBottom: 15,
            }}>
              {errorMessages.map((message, index) => (
                <Text 
                  key={index} 
                  style={{
                    fontSize: 16,
                    color: '#555',
                    marginBottom: 10,
                    textAlign: 'center',
                    lineHeight: 22, 
                  }}
                >
                  {message}
                </Text>
              ))}
            </View>
            
            <View style={{
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
              marginTop: 10,
            }}>
              {isSuccess ? (
                <Button 
                  mode="contained" 
                  onPress={() => {
                    setErrorModalVisible(false);
                    navigation.reset({
                      index: 0,
                      routes: [{ name: 'Login' }],
                    });
                  }}
                  style={{
                    marginVertical: 5,
                    backgroundColor: '#4CAF50',
                    width: '100%',
                  }}
                  icon="login"
                >
                  Giriş Ekranına Git
                </Button>
              ) : (
                <Button 
                  mode="contained" 
                  onPress={() => setErrorModalVisible(false)}
                  style={{
                    marginVertical: 5,
                    backgroundColor: '#FF9800',
                    width: '100%',
                  }}
                >
                  Tekrar Dene
                </Button>
              )}
              
              <Button 
                mode="outlined" 
                onPress={() => setErrorModalVisible(false)}
                style={{
                  marginVertical: 5,
                  width: '100%',
                }}
              >
                Kapat
              </Button>
            </View>
          </View>
        </View>
      )}
      
      {/* Başarılı Kayıt Manuel Yönlendirme Butonu - Daima görünür acil durum butonu */}
      {isSuccess && (
        <View style={styles.debugContainer}>
          <Button 
            mode="contained"
            onPress={() => {
              //console.log('Manuel login yönlendirme butonu tıklandı');
              navigation.reset({
                index: 0,
                routes: [{ name: 'Login' }],
              });
            }}
            style={styles.debugButton}
          >
            MANUEL GİRİŞ EKRANINA GİT
          </Button>
        </View>
      )}
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  scrollContent: {
    padding: 20,
  },
  headerContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  subtitle: {
    marginTop: 8,
    fontSize: 16,
    color: 'black',
  },
  formContainer: {
    width: '100%',
  },
  input: {
    marginBottom: 8,
    backgroundColor: '#fff',
    borderColor: '#ccc',
    color: '#000',
  },
  errorText: {
    color: '#e74c3c',
    fontSize: 12,
    marginLeft: 8,
    marginTop: -4,
    marginBottom: 8,
    fontWeight: 'bold',
  },
  divider: {
    marginVertical: 16,
  },
  registerButton: {
    marginTop: 16,
    height: 50,
    justifyContent: 'center',
    backgroundColor: 'rgb(125, 76, 236)',
    borderRadius: 8,
    elevation: 3,
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 20,
  },
  loginText: {
    fontSize: 16,
    color: 'black',
  },
  loginButton: {
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
  },
  buttonsContainer: {
    marginTop: 16,
  },
  snackbar: {
    position: 'absolute',
    bottom: 20,
    left: 10,
    right: 10,
    elevation: 10, // Android için yüksek elevation
    zIndex: 1000, // iOS için yüksek zIndex
    borderRadius: 8,
  },
  successSnackbar: {
    backgroundColor: '#4CAF50',
  },
  errorSnackbar: {
    backgroundColor: '#F44336',
  },
  // Modal stilleri
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modernModalContainer: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 20,
    width: '90%',
    alignItems: 'center',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  modalIcon: {
    marginVertical: 15,
    alignSelf: 'center',
  },
  modernModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 15,
    textAlign: 'center',
  },
  modernModalScrollContent: {
    maxHeight: 150,
    width: '100%',
    marginBottom: 15,
  },
  modernModalText: {
    fontSize: 16,
    color: '#555',
    marginBottom: 10,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalButtonContainer: {
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    marginTop: 10,
  },
  modalButton: {
    marginVertical: 5,
    borderRadius: 8,
    width: '100%',
  },
  // Eski stiller - uyumluluk için tutuldu
  modalContainer: {
    backgroundColor: 'white',
    margin: 20,
    borderRadius: 8,
    padding: 0,
    overflow: 'hidden',
    elevation: 5,
    maxWidth: '90%',
    alignSelf: 'center'
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  errorModalHeader: {
    backgroundColor: '#F44336',
  },
  successModalHeader: {
    backgroundColor: '#4CAF50',
  },
  modalTitle: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  closeButton: {
    margin: 0,
    padding: 0,
  },
  modalContent: {
    padding: 16,
    maxHeight: 300,
  },
  errorItem: {
    marginBottom: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#F44336',
    paddingLeft: 10,
  },
  successItem: {
    marginBottom: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#4CAF50',
    paddingLeft: 10,
  },
  errorItemText: {
    fontSize: 14,
    color: '#333',
  },
  // Debug stilleri
  debugContainer: {
    position: 'absolute',
    bottom: 80,
    left: 20,
    right: 20,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  debugButton: {
    backgroundColor: 'red',
    width: '100%',
    paddingVertical: 10,
    borderRadius: 5,
  }
});

export default RegisterScreen;
